import { useCallback, useMemo } from 'react';
import { Header, Table } from '@tanstack/react-table';
import { cn } from '@sib/ui';
import { DEFAULT_MIN_COLUMN_SIZE } from '../../constants';

interface DataTableHeaderResizeHandlerProps<T>
  extends React.HTMLAttributes<HTMLSpanElement> {
  table: Table<T>;
  header: Header<T, unknown>;
}

export const DataTableHeaderResizeHandler = <T,>({
  table,
  header,
  ...props
}: DataTableHeaderResizeHandlerProps<T>) => {
  const isRightPinned = header.column.getIsPinned() === 'right';
  const column = header.column;

  const getRightPinnedResizeHandler = useCallback(
    () => (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      const startX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const startSize = column.getSize();
      const minSize =
        column.columnDef.minSize ??
        column.columnDef.size ??
        DEFAULT_MIN_COLUMN_SIZE;
      const maxSize = column.columnDef.maxSize ?? Number.MAX_SAFE_INTEGER;

      const getClientX = (event: MouseEvent | TouchEvent) =>
        'touches' in event ? event.touches[0].clientX : event.clientX;

      const onMove = (moveEvent: MouseEvent | TouchEvent) => {
        const delta = getClientX(moveEvent) - startX;
        const newSize = Math.min(maxSize, Math.max(minSize, startSize - delta));
        table.setColumnSizing(prev => ({ ...prev, [column.id]: newSize }));
      };

      const onEnd = () => {
        table.setColumnSizingInfo(prev => ({
          ...prev,
          isResizingColumn: false,
        }));
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onEnd);
        window.removeEventListener('touchmove', onMove);
        window.removeEventListener('touchend', onEnd);
      };

      table.setColumnSizingInfo(prev => ({
        ...prev,
        isResizingColumn: column.id,
      }));

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onEnd);
      window.addEventListener('touchmove', onMove, {
        passive: false,
      });
      window.addEventListener('touchend', onEnd);
    },
    [table, column]
  );

  const resizeHandler = useMemo(
    () =>
      isRightPinned ? getRightPinnedResizeHandler() : header.getResizeHandler(),
    [isRightPinned, header, getRightPinnedResizeHandler]
  );

  return (
    <span
      onClick={e => e.stopPropagation()}
      onMouseDown={resizeHandler}
      onTouchStart={resizeHandler}
      className={cn(
        'user-select-none bg-secondary absolute top-0 z-50 block h-full w-[5px] cursor-col-resize touch-none rounded-xs opacity-0 transition-opacity duration-200 hover:opacity-100',
        isRightPinned
          ? 'left-0 rounded-b-lg rounded-bl-none'
          : 'right-0 rounded-b-lg rounded-br-none',
        column.getIsResizing() && 'bg-secondary opacity-100',
        props.className
      )}
    />
  );
};
